import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Copy, Check, Download, QrCode } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

/**
 * Promoter QR dialog.
 *
 * The QR `src` points at a backend endpoint that returns a PNG —
 * letting the browser handle caching and progressive rendering for
 * us. The "Download" button uses fetch() so we can rename the file
 * to the promoter's identifier; just <a download> would name it
 * "qr-code" which is unhelpful.
 */
export default function PromoterQRDialog({ open, onOpenChange, promoter }) {
  const [copied, setCopied] = useState(false);
  if (!promoter) return null;

  const base = (process.env.PUBLIC_BASE_URL || "https://torahya.com").replace(/\/$/, "");
  const link = `${base}/?ref=${promoter.referral_code}`;
  const qrUrl = api.adminPromoterQR(promoter.user_id);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("ההעתקה נכשלה");
    }
  };

  const downloadPng = async () => {
    try {
      const res = await fetch(qrUrl, { credentials: "include" });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeCode = (promoter.referral_code || "promoter").replace(/[^a-zA-Z0-9_-]/g, "");
      a.download = `torahya-qr-${safeCode}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error("הורדת ה-QR נכשלה");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="qr-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg flex items-center gap-2">
            <QrCode className="h-5 w-5 text-gold" strokeWidth={1.5} />
            קוד QR · {promoter.name || promoter.email}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col items-center gap-5 mt-2">
          <div className="bg-[#FAF6EC] p-4 rounded-md border border-gold/30">
            <img
              src={qrUrl}
              alt={promoter.name || promoter.referral_code}
              className="w-64 h-64"
              data-testid="qr-image"
            />
          </div>

          <div className="w-full space-y-2">
            <p className="text-xs uppercase tracking-wider text-muted-foreground text-center">
              קישור הפניה
            </p>
            <div className="flex items-center gap-2 bg-card/40 border border-border/60 rounded-sm p-2 text-xs" dir="ltr">
              <code className="flex-1 truncate font-mono text-foreground" data-testid="qr-link-text">
                {link}
              </code>
              <Button
                size="icon"
                variant="ghost"
                onClick={copy}
                className="h-7 w-7"
                data-testid="qr-copy-button"
              >
                {copied ? (
                  <Check className="h-3.5 w-3.5 text-emerald-500" strokeWidth={2} />
                ) : (
                  <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />
                )}
              </Button>
            </div>
          </div>

          <Button
            onClick={downloadPng}
            className="w-full"
            data-testid="qr-download-button"
          >
            <Download className="h-4 w-4 ml-2" strokeWidth={1.5} />
            הורד כ-PNG
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
